/**
 * The preferences window's own frame (见 21): the groups and pages the platform
 * declares before any module contributes.
 *
 * Only the frame lives here. What fills it — a page's sections, extra groups,
 * pages under `platform.apps`, sidebar actions — comes from modules through
 * `contributes.setting`. Container pages (`platform.account`,
 * `platform.security`) have no body of their own and
 * are hidden until something contributes a section. Pages the platform draws
 * itself (`platform.language`, …) carry a `builtin` section, rendered by the
 * window.
 *
 * Titles are platform message keys, not `%key%` placeholders: the platform has
 * no manifest, and its strings already live under the `platform:` namespace.
 */
import type { IconSpec } from './index';

export const SETTING_GROUP_PERSONAL = 'platform.personal';
export const SETTING_GROUP_GENERAL = 'platform.general';
export const SETTING_GROUP_APPS = 'platform.apps';
export const SETTING_GROUP_ABOUT = 'platform.about';

export const SETTING_PAGE_ACCOUNT = 'platform.account';
export const SETTING_PAGE_SECURITY = 'platform.security';
export const SETTING_PAGE_NOTIFICATIONS = 'platform.notifications';
export const SETTING_PAGE_LANGUAGE = 'platform.language';
export const SETTING_PAGE_APPEARANCE = 'platform.appearance';
export const SETTING_PAGE_SHORTCUTS = 'platform.shortcuts';
export const SETTING_PAGE_SLASH = 'platform.slash';
export const SETTING_PAGE_DEVELOPER = 'platform.developer';
export const SETTING_PAGE_ABOUT = 'platform.about';

/** Built-in section bodies the window draws. */
export type PlatformSettingBuiltin =
  | 'language'
  | 'appearance'
  | 'shortcuts'
  | 'slash'
  | 'developer'
  | 'about'
  | 'notifications';

export interface PlatformSettingGroup {
  readonly id: string;
  /** Header message key; absent draws the group without a header. */
  readonly titleKey?: string;
  readonly order: number;
}

export interface PlatformSettingPage {
  readonly id: string;
  readonly group: string;
  readonly titleKey: string;
  readonly icon: IconSpec;
  readonly order: number;
  /** The window's own body. Absent means a container page. */
  readonly builtin?: PlatformSettingBuiltin;
}

/** A section of platform-owned setting keys, rendered like a module's `keys` section. */
export interface PlatformSettingKeysSection {
  readonly id: string;
  readonly page: string;
  readonly titleKey?: string;
  readonly order: number;
  readonly keys: readonly string[];
}

export const PLATFORM_SETTING_GROUPS: readonly PlatformSettingGroup[] = [
  { id: SETTING_GROUP_PERSONAL, order: 100 },
  {
    id: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.general',
    order: 300,
  },
  { id: SETTING_GROUP_APPS, titleKey: 'settingsWindow.nav.apps', order: 400 },
  {
    id: SETTING_GROUP_ABOUT,
    titleKey: 'settingsWindow.nav.aboutSection',
    order: 900,
  },
];

const builtinIcon = (id: string): IconSpec => ({ type: 'builtin', id });

export const PLATFORM_SETTING_PAGES: readonly PlatformSettingPage[] = [
  {
    id: SETTING_PAGE_ACCOUNT,
    group: SETTING_GROUP_PERSONAL,
    titleKey: 'settingsWindow.nav.account',
    icon: builtinIcon('i-account'),
    order: 10,
  },
  {
    id: SETTING_PAGE_SECURITY,
    group: SETTING_GROUP_PERSONAL,
    titleKey: 'settingsWindow.nav.security',
    icon: builtinIcon('i-security-safe'),
    order: 20,
  },
  {
    id: SETTING_PAGE_NOTIFICATIONS,
    group: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.notifications',
    icon: builtinIcon('i-read-notification'),
    order: 10,
    builtin: 'notifications',
  },
  {
    id: SETTING_PAGE_LANGUAGE,
    group: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.language',
    icon: builtinIcon('i-global'),
    order: 20,
    builtin: 'language',
  },
  {
    id: SETTING_PAGE_APPEARANCE,
    group: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.appearance',
    icon: builtinIcon('i-appearance'),
    order: 30,
    builtin: 'appearance',
  },
  {
    id: SETTING_PAGE_SHORTCUTS,
    group: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.shortcuts',
    icon: builtinIcon('i-keyboard-outline'),
    order: 35,
    builtin: 'shortcuts',
  },
  {
    id: SETTING_PAGE_SLASH,
    group: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.slash',
    icon: builtinIcon('i-command'),
    order: 40,
    builtin: 'slash',
  },
  {
    id: SETTING_PAGE_DEVELOPER,
    group: SETTING_GROUP_GENERAL,
    titleKey: 'settingsWindow.nav.developer',
    icon: builtinIcon('i-bug'),
    order: 900,
    builtin: 'developer',
  },
  {
    id: SETTING_PAGE_ABOUT,
    group: SETTING_GROUP_ABOUT,
    titleKey: 'settingsWindow.nav.about',
    icon: builtinIcon('i-help'),
    order: 20,
    builtin: 'about',
  },
];

/**
 * Platform key rows on a preferences page. The Notifications page no longer
 * lists do-not-disturb, sound, or the taskbar flash here (见 23): that page is
 * the OS settings jump plus each module's own master switch. The three keys
 * stay in `PLATFORM_SETTINGS`. Do-not-disturb is still written by the tray.
 */
export const PLATFORM_SETTING_KEY_SECTIONS: readonly PlatformSettingKeysSection[] =
  [];

/** Group ids a module may reference without declaring. */
export const PLATFORM_SETTING_GROUP_IDS: ReadonlySet<string> = new Set(
  PLATFORM_SETTING_GROUPS.map((group) => group.id),
);

/** Page ids a module may reference without declaring. */
export const PLATFORM_SETTING_PAGE_IDS: ReadonlySet<string> = new Set(
  PLATFORM_SETTING_PAGES.map((page) => page.id),
);
